import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useSearchParams, Link } from 'react-router-dom';
import Hero from './components/Hero';
import PackageSelector from './components/PackageSelector';
import TimelineSelector from './components/TimelineSelector';
import PricingCalculator from './components/PricingCalculator';
import ProjectDetailsForm from './components/ProjectDetailsForm';
import ContactStep from './components/ContactStep';
import CompletionStep from './components/CompletionStep';
import CookieConsent from './components/CookieConsent';
import WebsiteDevelopment from './pages/WebsiteDevelopment';
import MobileApps from './pages/MobileApps';
import SEOOptimalisatie from './pages/SEOOptimalisatie';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Werkwijze from './pages/Werkwijze';
import AlgemeneVoorwaarden from './pages/AlgemeneVoorwaarden';
import IdeeTester from './pages/IdeeTester';
import IdeeTesterPromoCard from './components/IdeeTesterPromoCard';
import StatsStrip from './components/StatsStrip';
import { usePageTitle } from './hooks/usePageTitle';

function AppContent() {
  usePageTitle();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const stepParam = searchParams.get('step');
  const [currentStep, setCurrentStep] = useState(stepParam ? parseInt(stepParam) : 1);
  const [selectedPackage, setSelectedPackage] = useState<'webapp' | 'app' | 'both'>('webapp');
  const [timeline, setTimeline] = useState<'2weeks' | '1month' | 'superfast'>('1month');
  const [addons, setAddons] = useState({
    inlogRegistratie: false,
    androidIOS: false,
    extraIntegraties: false,
    seoOptimalisatie: false,
    googleAds: false,
    appStorePublicatie: false,
    betaalIntegratie: false,
    pushNotificaties: false,
    pages5to10: false,
    pages10plus: false,
    adminDashboard: false,
    emailFlows: false,
  });
  const [projectDetails, setProjectDetails] = useState({
    projectName: '',
    buildType: [] as string[],
    description: '',
    hasDesign: false,
    deadline: '',
  });

  // Sync currentStep with URL parameter and ensure step is always in URL
  useEffect(() => {
    if (stepParam) {
      const step = parseInt(stepParam);
      if (step >= 1 && step <= 4) {
        setCurrentStep(step);
      }
    } else {
      // If no step parameter, redirect to step 1
      navigate('/?step=1', { replace: true });
    }
  }, [stepParam, navigate]);

  const handleNextStep = () => {
    const nextStep = currentStep + 1;

    // When moving from Step 1 to Step 2, update project details based on selections
    if (currentStep === 1) {
      // Set buildType based on selectedPackage
      const buildTypes: string[] = [];
      if (selectedPackage === 'webapp') {
        buildTypes.push('webapp');
      }
      if (selectedPackage === 'app' || selectedPackage === 'both') {
        buildTypes.push('app');
      }
      if (selectedPackage === 'both') {
        buildTypes.push('website');
      }

      // Calculate suggested deadline based on timeline
      const today = new Date();
      let suggestedDeadline = new Date();

      if (timeline === '2weeks') {
        suggestedDeadline.setDate(today.getDate() + 14);
      } else if (timeline === '1month') {
        suggestedDeadline.setMonth(today.getMonth() + 1);
      } else if (timeline === 'superfast') {
        suggestedDeadline.setDate(today.getDate() + 7);
      }

      const deadlineStr = suggestedDeadline.toISOString().split('T')[0];

      setProjectDetails(prev => ({
        ...prev,
        buildType: buildTypes,
        deadline: deadlineStr
      }));
    }

    setCurrentStep(nextStep);
    navigate(`/?step=${nextStep}`);
    // Scroll to top when navigating forward
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevStep = () => {
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    navigate(`/?step=${prevStep}`);
    // Don't scroll - let user return to where they were
  };

  const scrollToPackages = () => {
    const element = document.getElementById('packages');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Blue Textured Background */}
      <div
        className={`relative overflow-hidden ${currentStep === 1 ? 'rounded-b-3xl' : ''}`}
        style={{
          backgroundImage: `url('/hero-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Navigation */}
        <nav className={`relative z-10 px-8 pb-10 sm:pb-0 pt-1 flex justify-between items-center ${currentStep === 4 ? 'bg-black/70 backdrop-blur-sm' : ''}`}>
          <div className="flex items-center">
            <img src="/logo512.png" alt="SnapLogic" className="h-12 w-auto sm:hidden" />
            <img src="/snaplogic-logo.png" alt="SnapLogic" className="hidden sm:block h-44 w-auto" />
          </div>

          <div className="flex items-center space-x-3 sm:space-x-8">
            <a href="/" className="relative text-white text-sm sm:text-base font-bold hover:text-orange-400 transition group">
              Home
              <div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                }}
              />
            </a>
            <div className="relative group">
              <span className="relative text-white text-sm sm:text-base font-bold hover:text-orange-400 transition cursor-pointer">
                Diensten
                <div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                  }}
                />
              </span>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <Link to="/diensten/website-development" className="block px-4 py-3 text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition rounded-t-lg">
                  Website Development
                </Link>
                <Link to="/diensten/mobile-apps" className="block px-4 py-3 text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition">
                  Mobile Apps
                </Link>
                <Link to="/diensten/seo-optimalisatie" className="block px-4 py-3 text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition">
                  SEO Optimalisatie
                </Link>
                <Link to="/idee-tester" className="block px-4 py-3 text-gray-800 hover:bg-orange-50 hover:text-orange-600 transition rounded-b-lg">
                  Idee Tester
                </Link>
              </div>
            </div>
            <Link to="/werkwijze" className="relative text-white text-sm sm:text-base font-bold hover:text-orange-400 transition group">
              Werkwijze
              <div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                }}
              />
            </Link>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="relative text-white text-sm sm:text-base font-bold hover:text-orange-400 transition group"
            >
              Contact
              <div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                }}
              />
            </button>
            {currentStep === 1 && (
              <button
                onClick={scrollToPackages}
                className="hidden lg:block text-white px-6 py-2 rounded-lg font-bold transition transform hover:scale-105"
                style={{
                  background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                }}
              >
                Offerte Aanvragen
              </button>
            )}
          </div>
        </nav>

        {/* Hero Content */}
        {currentStep === 1 && <Hero onGetStarted={scrollToPackages} />}
      </div>

      {/* Main Content with Light Gray Background */}
      <main className="relative" style={{ backgroundColor: '#eeedf5' }}>
        {currentStep === 1 && (
          <>
            <StatsStrip />

            <section id="packages" className="container mx-auto px-8 py-16">
              <IdeeTesterPromoCard />

              <PackageSelector
                selectedPackage={selectedPackage}
                setSelectedPackage={setSelectedPackage}
              />

              <TimelineSelector
                timeline={timeline}
                setTimeline={setTimeline}
              />

              <PricingCalculator
                selectedPackage={selectedPackage}
                timeline={timeline}
                addons={addons}
                setAddons={setAddons}
                onNext={handleNextStep}
              />
            </section>
          </>
        )}

        {currentStep === 2 && (
          <div style={{
            backgroundImage: `url('/hero-background.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            minHeight: '100vh'
          }}>
            <ProjectDetailsForm
            projectDetails={projectDetails}
            setProjectDetails={setProjectDetails}
            selectedPackage={selectedPackage}
            addons={addons}
            timeline={timeline}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            />
          </div>
        )}

        {currentStep === 3 && (
          <div style={{
            backgroundImage: `url('/hero-background.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            minHeight: '100vh'
          }}>
            <ContactStep
            onNext={handleNextStep}
            onPrev={handlePrevStep}
            projectDetails={projectDetails}
            selectedPackage={selectedPackage}
            timeline={timeline}
            addons={addons}
            />
          </div>
        )}

        {currentStep === 4 && (
          <div style={{
            backgroundImage: `url('/hero-background.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            minHeight: '100vh'
          }}>
            <CompletionStep
            projectDetails={projectDetails}
            selectedPackage={selectedPackage}
            addons={addons}
            timeline={timeline}
            />
          </div>
        )}
      </main>

      {/* Footer */}
      <footer
        id="contact"
        className="relative scroll-mt-4"
        style={{
          backgroundImage: `url('/hero-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="bg-black/70 backdrop-blur-sm">
          <div className="container mx-auto px-8 py-12">
            <div className="grid md:grid-cols-3 gap-8 text-white">
              {/* Logo Column */}
              <div>
                <img src="/snaplogic-logo.png" alt="SnapLogic" className="h-24 w-auto mb-4" />
                <p className="text-sm text-gray-300">
                  Jouw partner voor professionele websites en apps.
                </p>
              </div>

              {/* Services Column */}
              <div>
                <h3 className="font-bold text-lg mb-4">Diensten</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li><Link to="/diensten/website-development" className="hover:text-orange-400 transition">Website Development</Link></li>
                  <li><Link to="/diensten/mobile-apps" className="hover:text-orange-400 transition">Mobile Apps</Link></li>
                  <li><Link to="/diensten/seo-optimalisatie" className="hover:text-orange-400 transition">SEO Optimalisatie</Link></li>
                  <li><Link to="/idee-tester" className="hover:text-orange-400 transition">Idee Tester</Link></li>
                </ul>
              </div>

              {/* Contact Column */}
              <div>
                <h3 className="font-bold text-lg mb-4">Contact</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-center">
                    <span className="mr-2">📧</span>
                    info@snaplogic.nl
                  </li>
                  <li className="flex items-center">
                    <a
                      href="https://wa.me/31610654827"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center hover:text-orange-400 transition"
                    >
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="#25D366" viewBox="0 0 24 24" aria-label="WhatsApp">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      06-10654827
                    </a>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">📍</span>
                    <span>
                      Cornelis van der Lijnstraat 230<br />
                      2593 NP 's-Gravenhage
                    </span>
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">🏢</span>
                    KvK: 97231762
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-600 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-gray-400">
                © 2026 SnapLogic. Alle rechten voorbehouden.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <Link to="/privacy" className="text-sm text-gray-400 hover:text-orange-400 transition">Privacy Policy</Link>
                <Link to="/algemene-voorwaarden" className="text-sm text-gray-400 hover:text-orange-400 transition">Algemene Voorwaarden</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<AppContent />} />
        <Route path="/diensten/website-development" element={<WebsiteDevelopment />} />
        <Route path="/diensten/mobile-apps" element={<MobileApps />} />
        <Route path="/diensten/seo-optimalisatie" element={<SEOOptimalisatie />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/werkwijze" element={<Werkwijze />} />
        <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaarden />} />
        <Route path="/idee-tester" element={<IdeeTester />} />
      </Routes>
      <CookieConsent />
    </Router>
  );
}

export default App;
